import { useState, useEffect } from 'react';
import type { InvoiceContents, InvoiceSource, PaymentTerms } from '@jobos/shared';
import { DEFAULT_PAYMENT_TERMS } from '@/components/billing/PaymentTermsField';
import { useJobCashDefault } from '@/components/billing/CashInvoiceField';

/**
 * The shared type, not a local copy of the union: `InvoiceSource` is already
 * exactly `'custom' | 'lineItems'`, and it is the only reason
 * `setMode(contents.source)` typechecks. A hand-written copy would silently
 * break the day `INVOICE_SOURCES` gains a member.
 */
export type PartialInvoiceMode = InvoiceSource;

/**
 * Everything the partial-invoice form holds, for both of its jobs: creating a
 * new partial, and editing an existing one.
 *
 * Lives outside the dialog because the dialog is layout and submit. The seeding
 * rules here are the fiddly part — what a reopened dialog shows, and what an
 * edit opens on — and they are easier to reason about away from 300 lines of
 * JSX.
 */
export function usePartialInvoiceForm({
  jobId,
  defaultDescription,
  contents,
  open,
}: {
  jobId: string;
  defaultDescription: string;
  /** Edit mode: the invoice's current content, once the fetch lands. */
  contents?: InvoiceContents;
  /** Reseeding is keyed on this: see the effect below. */
  open: boolean;
}) {
  const [mode, setMode] = useState<PartialInvoiceMode>('custom');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState(defaultDescription);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Cash invoices get a 'C' in their number (INV-NC-26100-C3) and count on the
  // job's own separate cash sequence.
  // null = untouched, so the job's own invoice type decides. See
  // useJobCashDefault for why the job's answer is not seeded into state.
  const [cashChoice, setCashChoice] = useState<boolean | null>(null);
  const [paymentTerms, setPaymentTerms] = useState<PaymentTerms>(DEFAULT_PAYMENT_TERMS);

  const jobIsCash = useJobCashDefault(jobId);
  const isCash = cashChoice ?? jobIsCash ?? false;

  // Seed from the invoice being edited. Keyed on `open` AS WELL AS the invoice
  // id, and both are load-bearing. The id covers the action menu swapping which
  // invoice is open without the dialog closing in between, so a figure
  // abandoned on one does not greet the next. `open` covers the commoner case:
  // closing calls reset(), so without it, reopening the SAME invoice would show
  // create defaults — the id has not changed, so the effect would not re-run.
  useEffect(() => {
    if (!open || !contents) return;
    setMode(contents.source);
    setAmount(contents.source === 'custom' ? String(contents.amount) : '');
    setDescription(contents.description ?? '');
    setSelectedIds(contents.lineItemIds);
    setCashChoice(contents.isCash);
    setPaymentTerms(contents.paymentTerms ?? DEFAULT_PAYMENT_TERMS);
    // Deliberately NOT keyed on the `contents` object: a refetch (same invoice,
    // new reference) must not stomp on edits in progress.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contents?.invoiceId]);

  const changeMode = (next: PartialInvoiceMode) => {
    setMode(next);
    // Clear the other mode's state so switching back does not show stale input.
    // `description` is shared and meaningful in both, so it survives.
    if (next === 'custom') setSelectedIds([]);
    else setAmount('');
  };

  const toggleSelected = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const reset = () => {
    setMode('custom');
    setAmount('');
    setDescription(defaultDescription);
    setSelectedIds([]);
    // Back to following the job, NOT to false: on a cash job the next partial
    // should open cash again.
    setCashChoice(null);
    setPaymentTerms(DEFAULT_PAYMENT_TERMS);
  };

  return {
    mode, amount, description, selectedIds, isCash, paymentTerms,
    changeMode, setAmount, setDescription, toggleSelected, setCashChoice, setPaymentTerms,
    reset,
  };
}
